import { datadogLogs } from '@datadog/browser-logs';
import * as Sentry from '@sentry/react';

export enum LogLevel {
  DEBUG = 'debug',
  INFO = 'info',
  WARN = 'warn',
  // Error is handled separately
}

export interface LogMetadata {
  [key: string]: any;
  source?: string;
}

export interface LogDestination {
  name: string;
  log: (level: LogLevel, message: string, metadata?: LogMetadata) => Promise<void>;
  error: (error: Error, metadata?: LogMetadata) => Promise<void>;
  isEnabled: () => boolean;
}

class LoggingClient {
  private static instance: LoggingClient;
  private destinations: LogDestination[] = [];
  private isInitialized = false;

  private constructor() {
    // Private constructor to enforce singleton pattern
  }

  public static getInstance(): LoggingClient {
    if (!LoggingClient.instance) {
      LoggingClient.instance = new LoggingClient();
    }
    return LoggingClient.instance;
  }

  public initialize(destinations: LogDestination[]): void {
    if (this.isInitialized) {
      console.warn('LoggingClient already initialized');
      return;
    }

    this.destinations = destinations;
    this.isInitialized = true;
  }

  private async log(level: LogLevel, message: string, metadata?: LogMetadata): Promise<void> {
    const promises = this.destinations
      .filter((dest) => dest.isEnabled())
      .map((dest) => dest.log(level, message, metadata));

    try {
      await Promise.all(promises);
    } catch (e) {
      // Graceful no-op
    }
  }

  private async logError(error: unknown, metadata?: LogMetadata): Promise<void> {
    const normalizedError = this.normalizeError(error);

    const promises = this.destinations
      .filter((dest) => dest.isEnabled())
      .map((dest) => dest.error(normalizedError, metadata));

    try {
      await Promise.all(promises);
    } catch (e) {
      // Graceful no-op
    }
  }

  private normalizeError(error: unknown): Error {
    if (error instanceof Error) {
      return error;
    }

    if (typeof error === 'string') {
      return new Error(error);
    }

    if (error && typeof error === 'object' && 'message' in error) {
      return new Error(String((error as any).message));
    }

    return new Error(String(error));
  }

  public debug(message: string, metadata?: LogMetadata): Promise<void> {
    return this.log(LogLevel.DEBUG, message, metadata);
  }

  public info(message: string, metadata?: LogMetadata): Promise<void> {
    return this.log(LogLevel.INFO, message, metadata);
  }

  public warn(message: string, metadata?: LogMetadata): Promise<void> {
    return this.log(LogLevel.WARN, message, metadata);
  }

  public error(error: unknown, metadata?: LogMetadata): Promise<void> {
    return this.logError(error, metadata);
  }
}

export const SentryDestination: LogDestination = {
  name: 'sentry',
  isEnabled: () => process.env.NODE_ENV !== 'development', // Non-local only to avoid polluting Sentry logs
  log: async (level: LogLevel, message: string, metadata?: LogMetadata) => {
    Sentry.addBreadcrumb({
      message,
      level: level === LogLevel.WARN ? 'warning' : level,
      data: metadata,
    });
  },
  error: async (error: Error, metadata?: LogMetadata) => {
    Sentry.captureException(error, { level: 'error', extra: metadata });
  },
};

export const DatadogDestination: LogDestination = {
  name: 'datadog',
  isEnabled: () => process.env.NODE_ENV !== 'development', // Non-local only to avoid polluting DD logs
  log: async (level: LogLevel, message: string, metadata?: LogMetadata) => {
    const metadataObj = { metadata: { ...(metadata ?? {}) } };
    switch (level) {
      case LogLevel.DEBUG:
        datadogLogs.logger.debug(message, metadataObj);
        break;
      case LogLevel.INFO:
        datadogLogs.logger.info(message, metadataObj);
        break;
      case LogLevel.WARN:
        datadogLogs.logger.warn(message, metadataObj);
        break;
    }
  },
  error: async (error: Error, metadata?: LogMetadata) => {
    datadogLogs.logger.error(
      error.message,
      {
        metadata: { ...(metadata ?? {}), rawError: JSON.stringify(error, Object.getOwnPropertyNames(error)) },
      },
      error,
    );
  },
};

export const ConsoleDestination: LogDestination = {
  name: 'console',
  isEnabled: () => process.env.NODE_ENV === 'development',
  log: async (level: LogLevel, message: string, metadata?: LogMetadata) => {
    const log = `${message} ${!!metadata ? `(${JSON.stringify(metadata, null, 2)})` : ''}`;
    switch (level) {
      case LogLevel.DEBUG:
        // Commenting out to avoid spamming the console
        // console.debug(log);
        break;
      case LogLevel.INFO:
        console.info(log);
        break;
      case LogLevel.WARN:
        console.warn(log);
        break;
    }
  },
  error: async (error: Error, metadata?: LogMetadata) => {
    const log = `${error.message}${!!metadata ? ` (${JSON.stringify(metadata, null, 2)})` : ''}${
      error.stack ? `\n${error.stack}` : ''
    }`;
    console.error(log);
  },
};

const logger = LoggingClient.getInstance();
logger.initialize([SentryDestination, DatadogDestination, ConsoleDestination]);

export default logger;
